import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Chip,
  Button,
  DialogTitle,
} from "@mui/material";
import { MapPin, Briefcase, Globe, Settings, X } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import NttCard from "./NttCard";
import { useQuery } from "@tanstack/react-query";
import { fetchSowDashboard } from "../../api/nttSowApi";
import DataTable from "../DataTable/DataTable";

const Dashboard = () => {
  const [listModalOpen, setListModalOpen] = useState(false);
  const [listType, setListType] = useState("");

  const {
    data: response,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["sowdashboard"],
    queryFn: fetchSowDashboard,
  });

  const sowdashboard = response;

  const handleCardClick = (type) => {
    console.log("type, type", type)
    setListType(type);
    setListModalOpen(true);
  };

  const closeListModal = () => {
    setListModalOpen(false);
    setListType("");
  };

  const locationRows =
  (sowdashboard?.locations || []).map((loc, index) => ({
    id: index + 1,
    address: loc.address || "-",
    city: loc.city || "-",
    state: loc.state || "-",
    country: loc.country || "-"
  }));

  const locationColumns = [
  // {
  //   field: "id",
  //   headerName: "#",
  //   width: 70,
  // },
  {
    field: "address",
    headerName: "Address",
    flex: 2,
  },
  {
    field: "city",
    headerName: "City",
    flex: 1,
  },
  {
    field: "state",
    headerName: "State",
    flex: 1,
  },
  {
    field: "country",
    headerName: "Country",
    flex: 1,
  },
];

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Error loading dashboard data. Please try again.</Typography>
      </Box>
    );
  }

  const listData = listType === "locations" ? sowdashboard?.locations : sowdashboard?.services;
  const listCount = listType === "locations" ? sowdashboard?.locations_count : sowdashboard?.services_count;

  return (
    <Box sx={{ p: 2, bgcolor: "#f9f9f9", minHeight: "100vh" }}>
      <Toaster position="top-right" reverseOrder={false} />

      {/* Header Card */}
      <Card
        sx={{
          mx: "auto",
          mb: 2,
          borderRadius: 3,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          backdropFilter: "blur(10px)",
          background: "rgba(255, 255, 255, 0.8)",
          border: "1px solid rgba(255, 255, 255, 0.3)",
        }}
      >
        <CardContent
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={600} color="primary" textAlign="center">
            NTT Dashboard
          </Typography>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Box my={2}>
        <NttCard sowdashboard={sowdashboard} onCardClick={handleCardClick} />
      </Box>

      {/* List Modal - Shows all locations or services */}
      {/* <Dialog
        open={listModalOpen}
        onClose={closeListModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 2, pt: 3, px: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {listType === "locations" ? (
                <Globe size={28} color="#FF9800" style={{ marginRight: 12 }} />
              ) : (
                <Settings size={28} color="#9C27B0" style={{ marginRight: 12 }} />
              )}
              <Typography variant="h5" fontWeight={600}>
                {listType === "locations" ? "All Locations" : "All Services"} ({listCount || 0})
              </Typography>
            </Box>
            <IconButton onClick={closeListModal} sx={{ "&:hover": { bgcolor: "#f5f5f5" } }}>
              <X size={24} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 4, pb: 4 }}>
          {!listData || listData.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
              }}
            >
              {listType === "locations" ? (
                <MapPin size={48} color="#ccc" style={{ marginBottom: 16 }} />
              ) : (
                <Briefcase size={48} color="#ccc" style={{ marginBottom: 16 }} />
              )}
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No {listType === "locations" ? "Locations" : "Services"} Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                There are currently no {listType === "locations" ? "locations" : "services"} available to display.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: listType === "locations" ? "grid" : "block",
                gridTemplateColumns: listType === "locations" ? "repeat(auto-fill, minmax(200px, 1fr))" : "1fr",
                gap: 1.5,
                maxHeight: 500,
                overflowY: "auto",
                pr: 1,
              }}
            >
              {listData.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    bgcolor: "#f5f5f5",
                    p: 2,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    mb: listType === "services" ? 1 : 0,
                  }}
                >
                  {listType === "locations" ? (
                    <MapPin size={18} color="#FF9800" style={{ marginRight: 10, flexShrink: 0 }} />
                  ) : (
                    <Briefcase size={18} color="#9C27B0" style={{ marginRight: 10, flexShrink: 0 }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: listType === "services" ? "monospace" : "inherit",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: listType === "locations" ? "nowrap" : "normal",
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog> */}
      <Dialog
  open={listModalOpen}
  onClose={closeListModal}
  maxWidth="md"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: 4,
      boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
    },
  }}
>
  <DialogTitle sx={{ pb: 2, pt: 3, px: 4 }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {listType === "locations" ? (
          <Globe size={28} color="#FF9800" style={{ marginRight: 12 }} />
        ) : (
          <Settings size={28} color="#9C27B0" style={{ marginRight: 12 }} />
        )}
        <Typography variant="h5" fontWeight={600}>
          {listType === "locations" ? "All Locations" : "All Services"} ({listCount || 0})
        </Typography>
      </Box>

      <IconButton onClick={closeListModal} sx={{ "&:hover": { bgcolor: "#f5f5f5" } }}>
        <X size={24} />
      </IconButton>
    </Box>
  </DialogTitle>

  <DialogContent sx={{ px: 4, pb: 4 }}>
    {!listData || listData.length === 0 ? (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        {listType === "locations" ? (
          <MapPin size={48} color="#ccc" style={{ marginBottom: 16 }} />
        ) : (
          <Briefcase size={48} color="#ccc" style={{ marginBottom: 16 }} />
        )}
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No {listType === "locations" ? "Locations" : "Services"} Found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          There are currently no {listType === "locations" ? "locations" : "services"} available to display.
        </Typography>
      </Box>
    ) : (
      <>
        {listType === "locations" ? (
          <DataTable
            columns={locationColumns}
            rows={locationRows}
            height={500}
          />
        ) : (
          <Box
            sx={{
              maxHeight: 500,
              overflowY: "auto",
              pr: 1,
            }}
          >
            {listData.map((item, index) => (
              <Box
                key={index}
                sx={{
                  bgcolor: "#f5f5f5",
                  p: 2,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Briefcase size={18} color="#9C27B0" style={{ marginRight: 10, flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                  }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </>
    )}
  </DialogContent>
</Dialog>

    </Box>
  );
};

export default Dashboard;